import { useState, useEffect } from "react";
import { Star, GitFork, Eye, Activity, Tag, Code, AlertCircle, CheckCircle, Users, Calendar } from "lucide-react";
import { getEnvVar } from "@/lib/env";
import { fetchGitHubAPI, safeJsonParse } from "@/lib/skills";

interface GitHubStats {
  stars: number;
  forks: number;
  watchers: number;
  openIssues: number;
  closedIssues: number;
  totalIssues: number;
  releases: number;
  lastActivity: string;
  firstCommit: string;
  contributors: number;
  linesOfCode?: number;
  languages: Array<{ name: string; percentage: number; bytes: number }>;
  loading: boolean;
  error?: string;
}

interface GitHubStatsDisplayProps {
  repoUrl: string;
}

export default function GitHubStatsDisplay({ repoUrl }: GitHubStatsDisplayProps) {
  const [stats, setStats] = useState<GitHubStats>({
    stars: 0,
    forks: 0,
    watchers: 0,
    openIssues: 0,
    closedIssues: 0,
    totalIssues: 0,
    releases: 0,
    lastActivity: '',
    firstCommit: '',
    contributors: 0,
    languages: [],
    loading: true
  });

  useEffect(() => {
    const fetchGitHubStats = async () => {
      try {
        // Extract owner and repo from GitHub URL
        const match = repoUrl.match(/github\.com\/([^\/]+)\/([^\/]+)/);
        if (!match) {
          setStats(prev => ({ ...prev, loading: false, error: 'Invalid GitHub URL format' }));
          return;
        }

        const [, owner, repo] = match;
        const cleanRepo = repo.replace(/\.git$/, '').replace(/\/$/, ''); // Remove .git suffix and trailing slash if present

        // Create headers with authentication if token is available
        const headers: HeadersInit = {
          'Accept': 'application/vnd.github.v3+json',
          'User-Agent': 'PersonalPortfolio'
        };

        // Add authentication header if token is available
        const githubToken = getEnvVar('VITE_GITHUB_TOKEN');
        if (githubToken) {
          headers['Authorization'] = `Bearer ${githubToken}`;
        }

        // Fetch repository data and contributors with rate limiting
        const [repoResponse, releasesResponse, contributorsResponse] = await Promise.all([
          fetchGitHubAPI(`https://api.github.com/repos/${owner}/${cleanRepo}`, headers),
          fetchGitHubAPI(`https://api.github.com/repos/${owner}/${cleanRepo}/releases`, headers),
          fetchGitHubAPI(`https://api.github.com/repos/${owner}/${cleanRepo}/contributors`, headers)
        ]);

        if (!repoResponse.ok) {
          const errorData = await repoResponse.json().catch(() => ({}));
          console.error('GitHub API error:', repoResponse.status, errorData);
          
          if (repoResponse.status === 404) {
            throw new Error(`Repository "${owner}/${cleanRepo}" not found`);
          } else if (repoResponse.status === 403) {
            // Check if it's rate limiting
            if (errorData.message && errorData.message.includes('rate limit')) {
              const hasToken = !!githubToken;
              throw new Error(`GitHub API rate limit exceeded. ${hasToken ? 'Token detected but still rate limited.' : 'No authentication token found.'} Stats temporarily unavailable.`);
            } else {
              throw new Error('Repository is private or access denied');
            }
          } else {
            throw new Error(`GitHub API error: ${repoResponse.status}`);
          }
        }

        const repoData = await safeJsonParse(repoResponse);
        const releasesData = await safeJsonParse(releasesResponse);
        const contributorsData = await safeJsonParse(contributorsResponse);
        
        // Get first commit date
        let firstCommit = '';
        try {
          const commitsResponse = await fetchGitHubAPI(
            `https://api.github.com/repos/${owner}/${cleanRepo}/commits?per_page=1&page=1`,
            headers
          );
          if (commitsResponse.ok) {
            const commitsData = await safeJsonParse(commitsResponse);
            if (commitsData.length > 0) {
              // Get the first commit by going to the last page
              const linkHeader = commitsResponse.headers.get('Link');
              if (linkHeader) {
                const lastPageMatch = linkHeader.match(/page=(\d+)>; rel="last"/);
                if (lastPageMatch) {
                  const lastPage = parseInt(lastPageMatch[1]);
                  const firstCommitResponse = await fetchGitHubAPI(
                    `https://api.github.com/repos/${owner}/${cleanRepo}/commits?per_page=1&page=${lastPage}`,
                    headers
                  );
                  if (firstCommitResponse.ok) {
                    const firstCommitData = await safeJsonParse(firstCommitResponse);
                    if (firstCommitData.length > 0) {
                      firstCommit = firstCommitData[firstCommitData.length - 1].commit.committer.date;
                    }
                  }
                }
              } else {
                // No pagination, repository has only one page of commits
                firstCommit = commitsData[commitsData.length - 1]?.commit.committer.date || commitsData[0].commit.committer.date;
              }
            }
          }
        } catch (error) {
          console.warn('Failed to fetch first commit:', error);
        }
        
        // Get total closed issues (excluding pull requests)
        let totalClosedIssues = 0;
        try {
          let page = 1;
          let hasMoreIssues = true;
          
          while (hasMoreIssues) {
            const issuesResponse = await fetchGitHubAPI(
              `https://api.github.com/repos/${owner}/${cleanRepo}/issues?state=closed&per_page=100&page=${page}`,
              headers
            );
            
            if (issuesResponse.ok) {
              const issuesData = await safeJsonParse(issuesResponse);
              if (issuesData.length === 0) {
                hasMoreIssues = false;
              } else {
                // Filter out pull requests - only count actual issues
                const actualIssues = issuesData.filter((item: any) => !item.pull_request);
                totalClosedIssues += actualIssues.length;
                page++;
              }
            } else {
              hasMoreIssues = false;
            }
          }
        } catch (error) {
          console.warn('Failed to fetch closed issues:', error);
        }

        // Get open issues count (excluding pull requests)
        let openIssuesCount = 0;
        try {
          const openIssuesResponse = await fetchGitHubAPI(
            `https://api.github.com/repos/${owner}/${cleanRepo}/issues?state=open&per_page=100`,
            headers
          );
          
          if (openIssuesResponse.ok) {
            const openIssuesData = await safeJsonParse(openIssuesResponse);
            // Filter out pull requests - only count actual issues
            openIssuesCount = openIssuesData.filter((item: any) => !item.pull_request).length;
          }
        } catch (error) {
          console.warn('Failed to fetch open issues:', error);
        }

        const totalIssues = openIssuesCount + totalClosedIssues;

        // Try to fetch lines of code and languages data
        let linesOfCode;
        let languages: Array<{ name: string; percentage: number; bytes: number }> = [];
        try {
          const languagesResponse = await fetch(
            `https://api.github.com/repos/${owner}/${cleanRepo}/languages`,
            { headers }
          );
          if (languagesResponse.ok) {
            const languagesData = await languagesResponse.json();
            const totalBytes = Object.values(languagesData).reduce((sum: number, bytes: any) => sum + bytes, 0);
            
            // Convert to array with percentages and sort by usage
            languages = Object.entries(languagesData)
              .map(([name, bytes]: [string, any]) => ({
                name,
                bytes,
                percentage: Math.round((bytes / totalBytes) * 100)
              }))
              .sort((a, b) => b.bytes - a.bytes)
              .slice(0, 5); // Keep only top 5 languages
            
            // Rough estimate: sum of all language bytes divided by average bytes per line (≈50)
            linesOfCode = Math.round(totalBytes / 50);
          }
        } catch (error) {
          // Languages/lines of code estimation failed, continue without it
        }

        setStats({
          stars: repoData.stargazers_count,
          forks: repoData.forks_count,
          watchers: repoData.watchers_count,
          openIssues: openIssuesCount,
          closedIssues: totalClosedIssues,
          totalIssues,
          releases: releasesData.length,
          lastActivity: repoData.pushed_at,
          firstCommit,
          contributors: contributorsData.length,
          linesOfCode,
          languages,
          loading: false
        });

      } catch (error) {
        console.error('Failed to fetch GitHub stats:', error);
        setStats(prev => ({ 
          ...prev, 
          loading: false, 
          error: error instanceof Error ? error.message : 'Failed to fetch GitHub stats' 
        }));
      }
    };

    if (repoUrl && repoUrl.includes('github.com')) {
      fetchGitHubStats();
    } else {
      setStats(prev => ({ ...prev, loading: false, error: 'Invalid GitHub URL provided' }));
    }
  }, [repoUrl]);

  if (stats.loading) {
    return (
      <div className="grid grid-cols-5 gap-3 mt-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 animate-pulse">
            <div className="w-4 h-4 bg-muted rounded"></div>
            <div className="w-12 h-4 bg-muted rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (stats.error) {
    return (
      <div className="mt-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
        <div className="font-medium mb-1">GitHub Stats Unavailable</div>
        <div>{stats.error}</div>
        {stats.error.includes('private') && (
          <div className="mt-2 text-xs opacity-80">
            💡 Private repositories require authentication to access stats
          </div>
        )}
        {stats.error.includes('rate limit') && (
          <div className="mt-2 text-xs opacity-80">
            💡 GitHub API rate limit exceeded. Please try again in an hour or use authentication for higher limits
          </div>
        )}
        {stats.error.includes('not found') && (
          <div className="mt-2 text-xs opacity-80">
            💡 Repository may be private, deleted, or the URL might be incorrect
          </div>
        )}
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.round(diffDays / 30)} months ago`;
    return `${Math.round(diffDays / 365)} years ago`;
  };

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  return (
    <div className="grid grid-cols-5 gap-3 mt-3">
      {/* First row: stars, forks, watchers, contributors, releases */}
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Star className="w-4 h-4 text-yellow-500" />
        <span className="text-sm font-medium">{formatNumber(stats.stars)}</span>
        <span className="text-xs text-muted-foreground">stars</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <GitFork className="w-4 h-4 text-blue-500" />
        <span className="text-sm font-medium">{formatNumber(stats.forks)}</span>
        <span className="text-xs text-muted-foreground">forks</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Eye className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-medium">{formatNumber(stats.watchers)}</span>
        <span className="text-xs text-muted-foreground">watchers</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Users className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-medium">{stats.contributors}</span>
        <span className="text-xs text-muted-foreground">contributors</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Tag className="w-4 h-4 text-green-500" />
        <span className="text-sm font-medium">{stats.releases}</span>
        <span className="text-xs text-muted-foreground">releases</span>
      </div>
      
      {/* Second row: last activity, first commit, open issues, closed issues, lines of code */}
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Activity className="w-4 h-4 text-orange-500" />
        <span className="text-xs text-muted-foreground">last activity</span>
        <span className="text-sm font-medium">{formatDate(stats.lastActivity)}</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <Calendar className="w-4 h-4 text-purple-600" />
        <span className="text-xs text-muted-foreground">first commit</span>
        <span className="text-sm font-medium">{stats.firstCommit ? formatDate(stats.firstCommit) : 'N/A'}</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <AlertCircle className="w-4 h-4 text-red-500" />
        <span className="text-sm font-medium">{stats.openIssues}</span>
        <span className="text-xs text-muted-foreground">open issues</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium">{stats.closedIssues}</span>
        <span className="text-xs text-muted-foreground">closed issues</span>
      </div>
      
      {stats.linesOfCode ? (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
          <Code className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-medium">{formatNumber(stats.linesOfCode)}</span>
          <span className="text-xs text-muted-foreground">lines</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 opacity-50">
          <Code className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium">N/A</span>
          <span className="text-xs text-muted-foreground">lines</span>
        </div>
      )}
    </div>
  );
}
